import { useEffect, useMemo, useRef, useState } from 'react'

import { t } from '../i18n'
import type { RatingPoint } from '../types'

/** График уровня по матчам. Своя SVG-реализация без библиотек: одна линия,
 *  перекрестие с подсказкой по касанию, сетка по «круглым» уровням.
 *
 *  По оси X — номер матча, а не дата: игры идут неравномерно, и по времени
 *  неделя с пятью матчами слиплась бы в точку, а месяц отпуска растянул
 *  бы пустоту. Даты — в подписях по краям и в подсказке. */

const H = 190
const PAD = { top: 14, right: 14, bottom: 26, left: 38 }

function niceStep(range: number): number {
  if (range <= 0.6) return 0.1
  if (range <= 1.5) return 0.25
  if (range <= 3) return 0.5
  return 1
}

function fmtDelta(d: number): string {
  const sign = d > 0 ? '+' : d < 0 ? '−' : '±'
  return `${sign}${Math.abs(d).toFixed(2)}`
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(t().locale, { day: '2-digit', month: '2-digit' })
}

export default function LevelChart({ points }: { points: RatingPoint[] }) {
  const box = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(320)
  const [hover, setHover] = useState<number | null>(null)

  useEffect(() => {
    const el = box.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setWidth(Math.max(200, entry.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const geo = useMemo(() => {
    const levels = points.map((p) => p.level)
    const lo = Math.min(...levels)
    const hi = Math.max(...levels)
    const step = niceStep(hi - lo + 0.2)
    const min = Math.floor((lo - 0.05) / step) * step
    const max = Math.ceil((hi + 0.05) / step) * step
    const ticks: number[] = []
    for (let v = min; v <= max + 1e-9; v += step) ticks.push(Number(v.toFixed(2)))

    const innerW = width - PAD.left - PAD.right
    const innerH = H - PAD.top - PAD.bottom
    const x = (i: number) => PAD.left + (points.length === 1 ? innerW / 2 : (i * innerW) / (points.length - 1))
    const y = (v: number) => PAD.top + innerH - ((v - min) / (max - min || 1)) * innerH
    const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.level).toFixed(1)}`).join(' ')
    return { ticks, x, y, path, innerW }
  }, [points, width])

  function pick(clientX: number) {
    const el = box.current
    if (!el || points.length < 2) return
    const rel = clientX - el.getBoundingClientRect().left - PAD.left
    const i = Math.round((rel / geo.innerW) * (points.length - 1))
    setHover(Math.max(0, Math.min(points.length - 1, i)))
  }

  const last = points.length - 1
  const shown = hover ?? null
  const hp = shown !== null ? points[shown] : null
  const summary = `${t().levelChart}: ${points[0].level.toFixed(2)} → ${points[last].level.toFixed(2)}`

  // Подсказка не должна вылезать за края карточки и закрывать выбранную
  // точку: точка в верхней половине — подсказка внизу, и наоборот
  const tipLeft = shown !== null ? Math.min(Math.max(geo.x(shown) - 70, 0), width - 140) : 0
  const tipTop = shown !== null && geo.y(points[shown].level) < H / 2 ? H - PAD.bottom - 58 : 0

  return (
    <div ref={box} className="chart" style={{ position: 'relative' }}>
      <svg
        width={width}
        height={H}
        role="img"
        aria-label={summary}
        style={{ display: 'block', touchAction: 'pan-y' }}
        onPointerMove={(e) => pick(e.clientX)}
        onPointerDown={(e) => pick(e.clientX)}
        onPointerLeave={() => setHover(null)}
      >
        {geo.ticks.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={width - PAD.right} y1={geo.y(v)} y2={geo.y(v)} className="chart-grid" />
            <text x={PAD.left - 6} y={geo.y(v) + 4} textAnchor="end" className="chart-axis">
              {v.toFixed(2)}
            </text>
          </g>
        ))}

        <text x={PAD.left} y={H - 6} className="chart-axis">
          {shortDate(points[0].at)}
        </text>
        <text x={width - PAD.right} y={H - 6} textAnchor="end" className="chart-axis">
          {shortDate(points[last].at)}
        </text>

        <path d={geo.path} className="chart-line" />

        {shown !== null && (
          <line
            x1={geo.x(shown)}
            x2={geo.x(shown)}
            y1={PAD.top}
            y2={H - PAD.bottom}
            className="chart-cross"
          />
        )}

        {/* Точка текущего уровня — всегда; при касании — точка выбранного матча */}
        {[last, ...(shown !== null && shown !== last ? [shown] : [])].map((i) => (
          <circle key={i} cx={geo.x(i)} cy={geo.y(points[i].level)} r={4.5} className="chart-dot" />
        ))}
      </svg>

      {hp && (
        <div className="chart-tip" style={{ left: tipLeft, top: tipTop }}>
          <b>{hp.level.toFixed(2)}</b>
          {hp.delta != null ? (
            <span className={hp.delta > 0 ? 'badge' : hp.delta < 0 ? 'badge warn' : 'badge neutral'}>
              {fmtDelta(hp.delta)}
            </span>
          ) : (
            <span className="muted"> {t().chartStart}</span>
          )}
          <div className="muted small">
            {shortDate(hp.at)}
            {shown ? ` · #${shown}` : ''}
          </div>
        </div>
      )}
    </div>
  )
}
